import { Phone, Mail } from "lucide-react";
import { bookHref } from "@/utils/booking";
import { Link } from "react-router-dom";
import { trackEvent } from "@/utils/tracking";

const FOOTER_BOOK_URL = bookHref({ campaign: "footer", content: "footer-book" });

const FOOTER_SERVICES = [
  { name: "TV Mounting", slug: "tv-mounting" },
  { name: "Smart Home", slug: "smart-home" },
  { name: "IT Support", slug: "it-support" },
  { name: "Wi-Fi & Networks", slug: "wifi-network" },
  { name: "Home Theater", slug: "home-theater" },
  { name: "Business IT", slug: "business-it" },
  { name: "Yacht & Marine", slug: "yacht-marine" },
];

const SERVICE_AREAS = [
  { name: "Naples", slug: "naples" },
  { name: "North Naples", slug: "north-naples" },
  { name: "Port Royal", slug: "port-royal" },
  { name: "Pelican Bay", slug: "pelican-bay" },
  { name: "Marco Island", slug: "marco-island" },
  { name: "Bonita Springs", slug: "bonita-springs" },
  { name: "Estero", slug: "estero" },
  { name: "Fort Myers", slug: "fort-myers" },
  { name: "Miami (by appointment)", slug: "miami" },
  { name: "Cape Coral", slug: "cape-coral" },
];

const MIAMI_LUXURY_LINKS = [
  { name: "Luxury Smart Home Miami", slug: "luxury-smart-home-miami" },
  { name: "Sonos Installer Miami", slug: "sonos-installer-miami" },
  { name: "Home Theater Miami", slug: "home-theater-miami" },
  { name: "TV Mounting Miami", slug: "tv-mounting-miami" },
  { name: "Security Cameras Miami", slug: "security-camera-installation", to: "/miami/security-camera-installation" },
];


function FooterBrand() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <img
          src="/brand/logo-128.webp"
          srcSet="/brand/logo-128.webp 128w, /brand/logo-256.webp 256w"
          sizes="56px"
          loading="lazy"
          decoding="async"
          alt="I.T. Help of SWFL logo"
          width="56"
          height="56"
          className="h-14 w-auto object-contain"
        />
        <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <span className="text-blue-500">I.T.</span> Help of SWFL
        </span>
      </div>
      <p className="text-slate-500 text-sm leading-relaxed max-w-xs">
        White-glove smart home automation, luxury TV mounting, home theater, and yacht tech for Naples, Port Royal, Marco Island & Southwest Florida.
      </p>
    </div>
  );
}

function FooterContact() {
  return (
    <div>
      <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Contact</h4>
      <div className="space-y-1 md:space-y-3">
        <a href="tel:2398344377"
          onClick={() => trackEvent("call_click", { location: "footer" })}
          className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">
          <Phone size={16} /> 239-834-4377
        </a>
        <a href="mailto:jasont@techhelpswfl.com" className="flex items-center gap-2 min-h-[44px] md:min-h-0 py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">
          <Mail size={16} /> jasont@techhelpswfl.com
        </a>
        <a
          href={FOOTER_BOOK_URL}
          data-testid="footer-book-btn"
          onClick={() => trackEvent("book_now_click", { location: "footer" })}
          className="inline-flex items-center justify-center min-h-[44px] mt-3 bg-blue-600 text-white hover:bg-blue-700 font-medium px-5 py-2.5 rounded-lg transition-all text-sm btn-glow"
        >
          Book an Appointment
        </a>
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer data-testid="footer" className="site-footer bg-[#050A11] border-t border-white/5 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 mb-16">
          <FooterBrand />
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Services</h4>
            <ul className="space-y-1 md:space-y-2.5">
              {FOOTER_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">{s.name}</Link>
                </li>
              ))}
              <li className="pt-1 border-t border-white/5 mt-2">
                <Link to="/services" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-blue-400 hover:text-blue-300 transition-colors text-base md:text-sm font-medium">View all services →</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Service Areas</h4>
            <ul className="space-y-1 md:space-y-2.5">
              {SERVICE_AREAS.map((area) => (
                <li key={area.slug}>
                  <Link to={`/${area.slug}`} className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">{area.name}, FL</Link>
                </li>
              ))}
              <li className="pt-1 border-t border-white/5 mt-2">
                <Link to="/locations" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-blue-400 hover:text-blue-300 transition-colors text-base md:text-sm font-medium">All service areas →</Link>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-white/5">
              <p className="text-[11px] md:text-[10px] font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">
                Now serving Miami
              </p>
              <ul className="space-y-1 md:space-y-2" data-testid="footer-miami-links">
                {MIAMI_LUXURY_LINKS.map((link) => (
                  <li key={link.slug}>
                    <Link to={link.to || `/services/${link.slug}`} className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-sm md:text-xs">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Resources</h4>
            <ul className="space-y-1 md:space-y-2.5">
              <li><Link to="/blog" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">Blog</Link></li>
              <li><Link to="/help" data-testid="footer-help-center-link" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">Help Center</Link></li>
              <li><Link to="/app" data-testid="footer-support-app-link" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">Customer Support App</Link></li>
              <li><Link to="/urgent" data-testid="footer-urgent-link" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">Urgent Tech Help</Link></li>
              <li><Link to="/tools/tv-size-calculator" className="flex items-center min-h-[44px] md:min-h-0 md:block py-2 md:py-0 text-slate-400 md:text-slate-500 hover:text-white transition-colors text-base md:text-sm">TV Size Calculator</Link></li>
            </ul>
          </div>
          <FooterContact />
        </div>

        <div className="watermark-text text-center mb-8 select-none">
          I.T. HELP OF SWFL
        </div>

        <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-600 text-xs">
            &copy; {new Date().getFullYear()} I.T. Help of SWFL. All rights reserved. Licensed & Insured.
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="inline-flex items-center min-h-[44px] md:min-h-0 px-2 md:px-0 text-slate-500 md:text-slate-600 hover:text-slate-400 transition-colors text-sm md:text-xs">Privacy</Link>
            <Link to="/terms-of-service" data-testid="footer-terms-link" className="inline-flex items-center min-h-[44px] md:min-h-0 px-2 md:px-0 text-slate-500 md:text-slate-600 hover:text-slate-400 transition-colors text-sm md:text-xs">Terms</Link>
            <Link to="/shipping-policy" data-testid="footer-shipping-link" className="inline-flex items-center min-h-[44px] md:min-h-0 px-2 md:px-0 text-slate-400 hover:text-white transition-colors text-sm md:text-xs">Shipping Policy</Link>
          </div>
        </div>

        <div className="pt-4 mt-1 text-center md:text-left">
          <p className="text-slate-600 text-xs">
            Website created by{" "}
            <a
              href="https://jkmediacompany.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-developer-credit"
              className="text-slate-500 hover:text-white transition-colors duration-200 font-medium"
            >
              JK DIGITAL MEDIA SOLUTIONS, LLC
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
